import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faCalculator,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

// สไตล์สำหรับ DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "14px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "14px",
      fontWeight: "semibold",
      padding: "5px",
      textAlign: "center",
    },
  },
  cells: {
    style: {
      color: "gray",
      padding: "5px",
      fontSize: "12px",
      textAlign: "center",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// Date utility functions
const toThaiDateString = (date) => {
  if (!date || isNaN(date)) return "";
  const thaiYear = date.getFullYear() + 543;
  return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${thaiYear}`;
};

const toISODateString = (date) => {
  if (!date || isNaN(date)) return "";
  return date.toISOString().split("T")[0] + "T00:00:00.000Z";
};

const parseApiDate = (dateStr) => {
  if (!dateStr) return new Date();
  const date = new Date(dateStr);
  if (date.getFullYear() > 2500) {
    date.setFullYear(date.getFullYear() - 543);
  }
  return date;
};

const VaccineEstimateDistributionForm = () => {
  const navigate = useNavigate();
  const { sessionId, profile } = useAuth();
  const location = useLocation();
  const { transactionId } = useParams();

  // Initialize DocumentDate as Date object
  const today = new Date();
  const [formData, setFormData] = useState({
    BudgetYear: "",
    EstimateUnit: "",
    BudgetType: "งบจำหน่าย",
    TransactionID: transactionId || "ESD-XXXX-XX-XX",
    EstimateBy: profile?.Email || "",
    DocumentDate: today,
    VaccineDetails: [],
    Status: transactionId ? "" : "Draft",
  });
  const [offices, setOffices] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // สร้างตัวเลือกปีงบประมาณ
  const currentYear = new Date().getFullYear() + 543;
  const budgetYears = [currentYear, currentYear + 1];

  // เดือนในปีงบประมาณไทย (ต.ค. ถึง ก.ย.)
  const months = [
    { en: "Oct", th: "ต.ค.", offset: -1 },
    { en: "Nov", th: "พ.ย.", offset: -1 },
    { en: "Dec", th: "ธ.ค.", offset: -1 },
    { en: "Jan", th: "ม.ค.", offset: 0 },
    { en: "Feb", th: "ก.พ.", offset: 0 },
    { en: "Mar", th: "มี.ค.", offset: 0 },
    { en: "Apr", th: "เม.ย.", offset: 0 },
    { en: "May", th: "พ.ค.", offset: 0 },
    { en: "Jun", th: "มิ.ย.", offset: 0 },
    { en: "Jul", th: "ก.ค.", offset: 0 },
    { en: "Aug", th: "ส.ค.", offset: 0 },
    { en: "Sep", th: "ก.ย.", offset: 0 },
  ];

  // นิยามคอลัมน์สำหรับ DataTable
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>วัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: false,
    },
    ...months.map((month, index) => ({
      name: (
        <HStack justifyContent="center">
          <span>{`${month.th}${
            formData.BudgetYear
              ? (parseInt(formData.BudgetYear, 10) + month.offset - 2500)
                  .toString()
                  .padStart(2, "0")
              : ""
          }`}</span>
        </HStack>
      ),
      selector: (row) => row.MonthlyDemands[index],
      sortable: false,
      cell: (row) => (
        <Input
          type="number"
          value={row.MonthlyDemands[index]}
          onChange={(e) =>
            handleDemandChange(row.VaccineID, index, e.target.value)
          }
          min={0}
          size="xs"
          width="70px"
        />
      ),
      width: "70px",
    })),
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>รวม</span>
        </HStack>
      ),
      selector: (row) => row.TotalDemand,
      sortable: false,
      cell: (row) => row.TotalDemand.toLocaleString(),
      width: "100px",
    },
  ];

  // ดึงข้อมูลการประมาณการด้วย TransactionID
  const fetchVaccineEstimate = async () => {
    if (!transactionId) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineEstimateDistributionByID",
        data: { TransactionID: transactionId },
        sessionId: sessionId || "",
      });
      console.log("API Response:", response);
      if (response.result === "success") {
        const { VaccineEstimate, VaccineDetails } = response;
        const documentDate = parseApiDate(VaccineEstimate.DocumentDate);

        const updatedDetails = (VaccineDetails || []).map((detail) => {
          const monthlyDemands = [
            detail.OctQty || 0,
            detail.NovQty || 0,
            detail.DecQty || 0,
            detail.JanQty || 0,
            detail.FebQty || 0,
            detail.MarQty || 0,
            detail.AprQty || 0,
            detail.MayQty || 0,
            detail.JunQty || 0,
            detail.JulQty || 0,
            detail.AugQty || 0,
            detail.SepQty || 0,
          ];
          const totalDemand = monthlyDemands.reduce((sum, val) => sum + val, 0);
          return {
            VaccineID: detail.VaccineID,
            VaccineNameTH: detail.VaccineNameTH || "ไม่พบชื่อวัคซีน",
            MonthlyDemands: monthlyDemands,
            TotalDemand: totalDemand,
          };
        });

        setFormData({
          TransactionID: VaccineEstimate.TransactionID,
          DocumentDate: documentDate,
          BudgetType: VaccineEstimate.BudgetType || "",
          BudgetYear: VaccineEstimate.BudgetYear || "",
          EstimateUnit: VaccineEstimate.EstimateUnit || "",
          EstimateBy: VaccineEstimate.EstimateBy || profile?.Email || "",
          VaccineDetails: updatedDetails,
          Status: VaccineEstimate.Status || "Draft",
        });
      } else {
        console.warn("API returned non-success result:", response);
        alert("ไม่พบข้อมูลสำหรับ TransactionID นี้");
      }
    } catch (error) {
      console.error("Error fetching vaccine estimate:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // เปลี่ยนหน้าไปยัง login ถ้าไม่มี session
  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  // ดึงข้อมูลหน่วยงาน
  const fetchOffices = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeList",
        data: { OfficeType: "ปศข." },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOffices(response.OfficeList || []);
      } else {
        setOffices([]);
      }
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึงข้อมูลหน่วยงาน:", error);
      setOffices([]);
    }
  };

  // ดึงข้อมูลวัคซีน
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        //data: { ProductionFactory: formData.EstimateUnit || undefined },
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const vaccines = (response.VaccinesList || []).map((vaccine) => ({
          ...vaccine,
          MonthlyDemands: Array(12).fill(0),
          TotalDemand: 0,
        }));
        setVaccineOptions(vaccines);
        if (!transactionId) {
          setFormData((prev) => ({
            ...prev,
            VaccineDetails: vaccines,
          }));
        }
      } else {
        setVaccineOptions([]);
        if (!transactionId) {
          setFormData((prev) => ({
            ...prev,
            VaccineDetails: [],
          }));
        }
      }
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึงข้อมูลวัคซีน:", error);
      setVaccineOptions([]);
      if (!transactionId) {
        setFormData((prev) => ({
          ...prev,
          VaccineDetails: [],
        }));
      }
    }
  };

  // โหลดข้อมูลเมื่อเริ่มต้น
  useEffect(() => {
    if (sessionId) {
      Promise.all([fetchOffices(), fetchVaccineOptions()]).then(() => {
        if (transactionId) {
          fetchVaccineEstimate();
        }
      });
    }
  }, [sessionId, transactionId]);

  // รีเฟรชข้อมูลวัคซีนเมื่อ EstimateUnit เปลี่ยน
  useEffect(() => {
    if (sessionId && formData.EstimateUnit && !transactionId) {
      fetchVaccineOptions();
    }
  }, [formData.EstimateUnit, sessionId, transactionId]);

  useEffect(() => {
    if (profile?.OfficeType === "ปศข." && profile.OfficeID) {
      setFormData((prev) => ({ ...prev, EstimateUnit: profile.OfficeID }));
    }
  }, [profile]);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  // จัดการการเปลี่ยนแปลงข้อมูลความต้องการรายเดือน
  const handleDemandChange = (vaccineId, monthIndex, value) => {
    const newValue = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        const newMonthlyDemands = [...detail.MonthlyDemands];
        newMonthlyDemands[monthIndex] = newValue;
        const newTotalDemand = newMonthlyDemands.reduce(
          (sum, val) => sum + val,
          0
        );
        return {
          ...detail,
          MonthlyDemands: newMonthlyDemands,
          TotalDemand: newTotalDemand,
        };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  // ตรวจสอบความถูกต้องของฟิลด์
  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "BudgetYear":
        if (!value) {
          newErrors[name] = "กรุณาเลือกปีงบประมาณ";
        } else {
          delete newErrors[name];
        }
        break;
      case "EstimateUnit":
        if (!value) {
          newErrors[name] = "กรุณาเลือกหน่วยงาน";
        } else {
          delete newErrors[name];
        }
        break;
      case "BudgetType":
        if (!value) {
          newErrors[name] = "กรุณาเลือกประเภทงบประมาณ";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  // ตรวจสอบว่าฟอร์มครบถ้วนหรือไม่
  const isFormValid = () => {
    return (
      formData.BudgetYear &&
      formData.EstimateUnit &&
      formData.BudgetType &&
      Object.keys(errors).length === 0
    );
  };

  // จัดการการบันทึกฟอร์มเริ่มต้น
  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineEstimateDistribution",
        data: {
          ...formData,
          DocumentDate: toISODateString(formData.DocumentDate),
          VaccineDetails: [],
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({
          ...formData,
          TransactionID: response.TransactionID || "ESD-XXXX-XX-XX",
          Status: "Draft",
        });
        setIsSubmitted(true);
        alert(
          "บันทึกเอกสารสำเร็จ! เลขที่เอกสาร: " +
            (response.TransactionID || "ESP-XXXX-XX-XX")
        );
      }
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการบันทึกเอกสาร:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // จัดการการบันทึกข้อมูลตารางความต้องการ
  const handleDemandSubmit = async (updatedFormData = formData) => {
    setIsLoading(true);
    try {
      // Safeguard: Ensure VaccineDetails is an array
      const vaccineDetails = Array.isArray(updatedFormData.VaccineDetails)
        ? updatedFormData.VaccineDetails
        : [];
      // Create a clean payload with only necessary fields
      const payload = {
        TransactionID: updatedFormData.TransactionID,
        DocumentDate: toISODateString(updatedFormData.DocumentDate),
        BudgetType: updatedFormData.BudgetType,
        BudgetYear: updatedFormData.BudgetYear,
        EstimateUnit: updatedFormData.EstimateUnit,
        EstimateBy: updatedFormData.EstimateBy,
        Status: updatedFormData.Status,
        VaccineDetails: vaccineDetails.map((detail) => ({
          VaccineID: detail.VaccineID,
          VaccineName: detail.VaccineNameTH,
          MonthlyDemands: detail.MonthlyDemands,
          TotalDemand: detail.TotalDemand,
        })),
      };
      console.log("API Payload:", payload); // Debug payload
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineEstimateDistributionByID",
        data: payload,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกข้อมูลปริมาณความต้องการใช้วัคซีนและแอนติเจนสำเร็จ!");
        navigate("/vaccineestimatedistributionlist");
      }
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการบันทึกข้อมูลตาราง:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // จัดการการบันทึกและส่งเอกสาร
  const handleConfirmWithSendRequest = async () => {
    const updatedFormData = { ...formData, Status: "Completed" };
    setFormData(updatedFormData);
    await handleDemandSubmit(updatedFormData);
  };

  // คำนวณยอดรวมแต่ละคอลัมน์
  const calculateColumnTotals = () => {
    const totals = Array(12).fill(0);
    if (Array.isArray(formData.VaccineDetails)) {
      formData.VaccineDetails.forEach((detail) => {
        detail.MonthlyDemands.forEach((demand, index) => {
          totals[index] += demand;
        });
      });
    }
    const grandTotal = totals.reduce((sum, val) => sum + val, 0);
    return { monthlyTotals: totals, grandTotal };
  };

  const { monthlyTotals, grandTotal } = calculateColumnTotals();

  // ข้อมูลสำหรับแถวรวมทั้งสิ้น
  const totalRow = {
    VaccineID: "TOTAL",
    VaccineNameTH: "รวมทั้งสิ้น",
    MonthlyDemands: monthlyTotals,
    TotalDemand: grandTotal,
  };

  // ดึงชื่อหน่วยงานจาก OfficeID
  const officeName =
    offices.find((office) => office.OfficeID === formData.EstimateUnit)
      ?.OfficeName || "";

  // กรองรายการหน่วยงานตาม OfficeType ของผู้ใช้
  const officeOptions =
    profile?.OfficeType === "ปศข."
      ? offices.filter((office) => office.OfficeID === profile.OfficeID)
      : offices;
  // ===

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายการปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)" />

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลปริมาณความต้องการใช้วัคซีนและแอนติเจน (งบจำหน่าย)"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
                color="black"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่เอกสาร
              </FormLabel>
              <Input
                name="DocumentDate"
                value={toThaiDateString(formData.DocumentDate)}
                isReadOnly
                colorScheme="iBlue"
                color="black"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.BudgetType}>
              <FormLabel fontSize="xl" color="gray">
                ประเภทงบประมาณ
              </FormLabel>
              <Input
                name="BudgetType"
                value={formData.BudgetType}
                isReadOnly
                colorScheme="iBlue"
                color="black"
              />
              <FormErrorMessage>{errors.BudgetType}</FormErrorMessage>
            </FormControl>
          </GridItem>
          {/*
              <GridItem colSpan={1}>
                <FormControl isRequired isInvalid={!!errors.BudgetType} isDisabled={isSubmitted}>
                  <FormLabel fontSize="xl" color="gray">
                    ประเภทงบประมาณ
                  </FormLabel>
                  <Select
                    name="BudgetType"
                    value={formData.BudgetType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภทงบประมาณ"
                    colorScheme="iBlue"
                    color={isSubmitted ? "black" : "inherit"}
                  >
                    <option value="Prevention">งบป้องกัน</option>
                    <option value="Distribution">งบจำหน่าย</option>
                  </Select>
                  <FormErrorMessage>{errors.BudgetType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              */}
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.BudgetYear}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                ปีงบประมาณ
              </FormLabel>
              <Select
                name="BudgetYear"
                value={formData.BudgetYear}
                onChange={handleInputChange}
                placeholder="เลือกปีงบประมาณ"
                colorScheme="iBlue"
                color={isSubmitted ? "black" : "inherit"}
              >
                {budgetYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.BudgetYear}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.EstimateUnit} >
              <FormLabel fontSize="xl" color="gray">
                หน่วยงาน
              </FormLabel>
              <Select
                name="EstimateUnit"
                value={formData.EstimateUnit}
                onChange={handleInputChange}
                placeholder="เลือกหน่วยงาน"
                colorScheme="iBlue"
                color={isSubmitted || profile?.OfficeType === 'ปศข.' ? "black" : "inherit"}
              >
                {officeOptions.length > 0 ? (
                  officeOptions.map((office) => (
                    <option key={office.OfficeID} value={office.OfficeID}>
                      {office.OfficeName}
                    </option>
                  ))
                ) : (
                  <option disabled>ไม่มีข้อมูลหน่วยงาน</option>
                )}
              </Select>
              <FormErrorMessage>{errors.EstimateUnit}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ผู้จัดทำ
              </FormLabel>
              <Input
                name="EstimateBy"
                value={formData.EstimateBy}
                isReadOnly
                colorScheme="iBlue"
                color="black"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5}>
              <IconButton.Cancel
                onClick={() => navigate("/vaccineestimatedistributionlist")}
                label="ยกเลิก"
              />
              <IconButton.Submit
                label="บันทึก"
                onClick={handleSubmit}
                isLoading={isLoading}
              />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box mb={5} p={2}>
            <Heading size="md" fontWeight="normal">
              ปริมาณความต้องการใช้วัคซีนและแอนติเจน{" "}
              {formData.BudgetType === "Prevention" ? "งบป้องกัน" : "งบจำหน่าย"}{" "}
              ปีงบประมาณ {formData.BudgetYear} ของ {officeName} แสดงเป็นรายเดือน
              (หน่วย : โด๊ส)
            </Heading>
          </Box>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={[...formData.VaccineDetails, totalRow]}
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </Box>
          <Box p={5} textAlign="right">
            <HStack spacing={5}>
              <IconButton.Cancel
                onClick={() => navigate("/vaccineestimatedistributionlist")}
                label="ยกเลิก"
              />
              {formData.Status === "Draft" && (
                <IconButton.Save
                  label="บันทึกร่างเอกสาร"
                  onClick={() => handleDemandSubmit()}
                  isLoading={isLoading}
                />
              )}
              {formData.Status === "Draft" && (
                <IconButton.Send
                  label="บันทึกและส่งเอกสาร"
                  onClick={handleConfirmWithSendRequest}
                  isLoading={isLoading}
                />
              )}
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VaccineEstimateDistributionForm;
